import { useEffect, useMemo, useState } from 'react';

// Third-party
import isEmpty from 'lodash/isEmpty';

// Hooks
import { splittedFullName } from '@mgm/common/utils';
import { useAppSelector } from '../../../hooks/useAppStore';
import { useTeamPermissions } from './useTeamPermissions';
import { useOptions } from '../../../hooks/useOptions';

// Redux-toolkit
import { getSelectedMember } from '../../../store/team/selectors';
import { getCurrencyById } from '../../../store/common/selectors';

// Data
import { INITIAL_FORM, MEMBER_FIELDS } from '../data/team';

// Utils
import { getDiffBetweenNestedObj } from '../../../utils/helpers';

// Types
import { TEditUser, TPhone } from '../../../types';
import { usePermissions } from '../../../hooks/useCommon';

export function useEditMember() {
  const selectedMember = useAppSelector(getSelectedMember);
  const getCurrency = useAppSelector(getCurrencyById);
  const { roleMap, wageMap } = useOptions();
  const { teamPermissions } = useTeamPermissions();
  const { permissions } = usePermissions();

  const [formInputs, setFormInputs] = useState(INITIAL_FORM);
  const [prevFormInputs, setPrevFormInputs] = useState(INITIAL_FORM);
  const [mode, setMode] = useState('create');

  useEffect(() => {
    if (selectedMember?.email) {
      setMode(teamPermissions.isAllowManage && !permissions.isEmployee ? 'edit' : 'view');
      const { firstName, lastName } = splittedFullName(selectedMember.name);

      let userData = {
        currency: getCurrency(selectedMember.payment?.paymentCurrencyId),
        paymentAmount: selectedMember.payment?.paymentAmount,
        permissionLevel: roleMap.toValue(selectedMember.permissionLevel),
        paymentType: wageMap.toValue(selectedMember.payment?.paymentType as 'Hourly Wage' | 'Salary'),
        phone: {} as TPhone,
        pinCode: selectedMember.pinCode || '',
        shopifyId: selectedMember.shopifyId || '',
        commissionPlan: selectedMember.commissionPlan || null,
        clockIn: selectedMember.clockIn,
        commissionTags: selectedMember.commissionTags?.map((tag, index) => ({
          name: tag,
          index: index + 1,
        })) || null,
        isPromoterCommission: selectedMember.isPromoterCommission,
      } as unknown as TEditUser;

      if (typeof selectedMember.phone === 'object' && selectedMember.phone !== null && 'e164' in selectedMember.phone) {
        userData.phone.e164 = (selectedMember.phone as TPhone)?.e164 || '';
      } else {
        userData.phone.e164 = typeof selectedMember.phone === 'object' ? '' : selectedMember?.phone as string;
      }

      Object.entries(selectedMember).forEach(([key, value]) => {
        if (MEMBER_FIELDS.includes(key)) {
          if (key === 'name') {
            userData = { ...userData, firstName, lastName };
          }
          if (key === 'pinCode') {
            userData = { ...userData, pinCode: selectedMember.pinCode || '' };
          } else {
            userData = { ...userData, [key]: value };
          }
        }
      });

      setFormInputs(userData);
      setPrevFormInputs(userData);
    } else {
      setMode('create');
      setFormInputs(INITIAL_FORM);
    }
  }, [selectedMember]);

  const getFormDetails = useMemo(() => ({
    ...formInputs,
    phone: formInputs.phone?.e164 || '',
  }), [formInputs]);

  const isEdited = useMemo(() => {
    const diff = getDiffBetweenNestedObj(formInputs, prevFormInputs);

    return !isEmpty(diff);
  }, [formInputs, prevFormInputs]);

  return {
    isEdited,
    getFormDetails,
    setFormInputs,
    mode,
    formInputs,
  };
}
